import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import enTranslations from './translations/en.json'

// Register the React plugin synchronously at module load time.
// use-sync-external-store-shim (pulled in by react-i18next) is evaluated
// during module initialization, before any async code runs. Deferring .use()
// to an async function causes "Cannot read properties of undefined (reading 'useState')"
// in Vite production builds due to chunk evaluation order.
i18n.use(initReactI18next)

const SUPPORTED_LANGUAGES = {
  'en': ['en', 'en-US', 'en-GB', 'en-CA', 'en-AU'],
  'es-MX': ['es', 'es-MX', 'es-ES', 'es-AR', 'es-CO'],
  'fr-CA': ['fr', 'fr-CA', 'fr-FR', 'fr-BE'],
  'pt-BR': ['pt', 'pt-BR', 'pt-PT'],
  'ru': ['ru', 'ru-RU'],
  'zh-CN': ['zh', 'zh-CN', 'zh-Hans', 'zh-SG'],
  'de': ['de', 'de-DE', 'de-AT', 'de-CH'],
  'nl': ['nl', 'nl-NL', 'nl-BE']
}

function detectBrowserLanguage() {
  const browserLanguages = navigator.languages || [navigator.language || navigator.userLanguage || 'en']
  for (const browserLang of browserLanguages) {
    for (const [appLang, variants] of Object.entries(SUPPORTED_LANGUAGES)) {
      if (variants.some(v => browserLang.toLowerCase().startsWith(v.toLowerCase()))) return appLang
    }
  }
  return 'en'
}

async function loadLanguageBundle(lang) {
  if (lang === 'en' || i18n.hasResourceBundle(lang, 'translation')) return
  try {
    const module = await import(`./translations/${lang}.json`)
    i18n.addResourceBundle(lang, 'translation', module.default, true, true)
  } catch (e) {
    // Falls back to English via fallbackLng
  }
}

export async function initI18n() {
  const stored = localStorage.getItem('i18nextLng')
  const initialLanguage = (stored && SUPPORTED_LANGUAGES[stored]) ? stored : detectBrowserLanguage()

  const resources = { en: { translation: enTranslations } }
  if (initialLanguage !== 'en') {
    try {
      const mod = await import(`./translations/${initialLanguage}.json`)
      resources[initialLanguage] = { translation: mod.default }
    } catch (e) {
      // Falls back to English
    }
  }

  await i18n.init({
    resources,
    lng: initialLanguage,
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    react: { useSuspense: false }
  })

  const _origChangeLanguage = i18n.changeLanguage.bind(i18n)
  i18n.changeLanguage = async (lang) => {
    await loadLanguageBundle(lang)
    return _origChangeLanguage(lang)
  }

  // Persist language selection to localStorage on every change
  i18n.on('languageChanged', (lng) => {
    if (SUPPORTED_LANGUAGES[lng] && localStorage.getItem('i18nextLng') !== lng) {
      localStorage.setItem('i18nextLng', lng)
    }
  })
}

export default i18n